import { ErrorApi, ErrorHttp, ErrorTipoRespuesta } from './errores';
import * as protocolo from './protocolo';
import { concatenarUrl } from './utilidades';

type NombresDeComandos = keyof protocolo.MapaDeComandos;
type NombresDeComandosSinArgumentos = {
    [N in NombresDeComandos]: keyof protocolo.Argumentos<N> extends never ? N : never;
}[NombresDeComandos];
type NombresDeComandosConArgumentos = Exclude<NombresDeComandos, NombresDeComandosSinArgumentos>;

export interface ClienteApi {
    ping(): Promise<boolean>;

    ejecutar<N extends NombresDeComandosConArgumentos>(
        comando: N,
        argumentos: protocolo.Argumentos<N>,
    ): Promise<protocolo.Resultados<N>>;

    ejecutar<N extends NombresDeComandosSinArgumentos>(
        comando: N,
    ): Promise<protocolo.Resultados<N>>;
}

export type FetcherRequest = Readonly<{
    method: 'GET' | 'POST';
    url: string;
    headers: Readonly<Record<string, string>>;
    body?: string;
    timeout?: number;
}>;

export interface FetcherResponse {
    statusCode: number;
    headers: { get(name: string): string | undefined | null };
    body: unknown;
}

export type FetcherFunction = (this: void, request: FetcherRequest) => Promise<FetcherResponse>;

type Token = string;

export interface TokenStorage {
    get(): Token | undefined | null;
}

export function clienteApiHttp(
    opciones: Readonly<{
        baseUrl: string;
        fetcher: FetcherFunction;
        tokenStorage?: TokenStorage;
    }>,
): ClienteApi {
    const { baseUrl, fetcher, tokenStorage = { get: () => undefined } } = opciones;

    const PING_URL = concatenarUrl(baseUrl, '/ping');
    const API_URL = concatenarUrl(baseUrl, '/api');

    return {
        async ping() {
            const res = await fetcher({
                method: 'GET',
                url: PING_URL,
                headers: {},
            });

            return res.statusCode === 200;
        },

        async ejecutar(comando: string, argumentos?: unknown) {
            const res = await fetcher({
                method: 'POST',
                url: API_URL,
                headers: {
                    'Content-Type': 'application/json; charset=utf-8',
                },
                body: JSON.stringify({
                    tipo: 'peticion',
                    comando,
                    token: tokenStorage.get() || '',
                    argumentos,
                }),
            });

            if (res.statusCode !== 200) {
                throw new ErrorHttp(
                    res.statusCode,
                    `La respuesta HTTP tiene código ${res.statusCode}`,
                );
            }

            const contentType = res.headers.get('content-type');
            if (!contentType || contentType.toLowerCase().indexOf('json') === -1) {
                return lanzarErrorTipoRespuesta([
                    `La cabecera Content-Type es ${contentType}`,
                    `La respuesta tiene cuerpo: ${res.body}`,
                ]);
            }

            const respuesta = parseJson(res.body as string);
            if (!esRespuestaValida(respuesta)) {
                return lanzarErrorTipoRespuesta([
                    `El objeto no es una respuesta válida, sino: ${JSON.stringify(respuesta)}`,
                ]);
            }

            if (!respuesta.satisfactoria) {
                const { tipo, mensaje, detalles } = respuesta.error;
                throw new ErrorApi(respuesta.comando, tipo, mensaje, detalles);
            }

            return respuesta.resultados;
        },
    };
}

type RespuestaValida = protocolo.Respuesta<NombresDeComandos>;

function lanzarErrorTipoRespuesta(detalles: ReadonlyArray<string>): never {
    throw new ErrorTipoRespuesta(
        'Hubo un error durante la comunicación con el servidor o en el programa y' +
            ' se recibieron datos inesperados. Reinténtelo luego de unos segundos.' +
            ' Si el error persiste contacte al administrador.',
        detalles,
    );
}

function parseJson(json: string): Partial<RespuestaValida> {
    try {
        return JSON.parse(json);
    } catch (error) {
        return lanzarErrorTipoRespuesta([
            `La respuesta no es un JSON válido, sino: "${json}"`,
            `JSON.parse falló con: ${error}`,
        ]);
    }
}

// tslint:disable-next-line: no-any
function esRespuestaValida(objeto: any): objeto is RespuestaValida {
    return objeto.tipo === 'respuesta' && typeof objeto.satisfactoria === 'boolean';
}



// WEBPACK FOOTER //
// ./src/api/clienteApi/clienteApi.ts